import { forwardRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'

/**
 * The search at the top right of every list page: a 284px white field framed
 * in `primary-tonal` with an attached 40px square holding the search icon.
 *
 * The frame is one of the two borders the design actually draws — it is what
 * makes the search field a search field rather than another input — and it
 * steps up to `primary` while the field has focus.
 *
 * It is a real `<form role="search">`, so Enter submits without a key handler.
 * Figma shows no placeholder; a scoped one ("Dokumente durchsuchen") doubles
 * as the accessible name, and "Suchen" is the fallback.
 */
export interface FxSearchFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Called on submit — the button or Enter. It receives the field's current
   * value, which a zero-argument handler (the design system's contract) is
   * free to ignore.
   */
  onSearch?: (value: string) => void
  /** Applied to the form, which is the element carrying the size. */
  className?: string
  inputClassName?: string
}

export const FxSearchField = forwardRef<HTMLInputElement, FxSearchFieldProps>(
  ({ className, inputClassName, onSearch, placeholder, 'aria-label': ariaLabel, ...props }, ref) => {
    // Only so that an uncontrolled field can still hand its value to
    // `onSearch`; a controlled caller's `value` wins through `props`.
    const [typed, setTyped] = useState('')

    return (
      <form
        role="search"
        className={cn(
          'flex h-control w-search-width max-w-full overflow-hidden rounded-md border-2',
          'border-primary-tonal bg-surface shadow-raised focus-within:border-primary',
          className,
        )}
        onSubmit={(event) => {
          event.preventDefault()
          const input = event.currentTarget.elements.namedItem('q')
          onSearch?.(input instanceof HTMLInputElement ? input.value : typed)
        }}
      >
        <input
          ref={ref}
          name="q"
          type="search"
          aria-label={ariaLabel ?? placeholder ?? 'Suchen'}
          placeholder={placeholder}
          className={cn(
            'min-w-0 flex-1 bg-transparent px-4 text-body text-text outline-none',
            'placeholder:text-text-muted focus-visible:outline-none',
            inputClassName,
          )}
          {...props}
          onChange={(event) => {
            setTyped(event.target.value)
            props.onChange?.(event)
          }}
        />
        <button
          type="submit"
          aria-label="Suchen"
          className="flex w-control shrink-0 items-center justify-center bg-primary-tonal text-primary-text"
        >
          <FxIcon name="search" size={26} />
        </button>
      </form>
    )
  },
)
FxSearchField.displayName = 'FxSearchField'
